import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { analyticsLogger, IntegrationType, PageType } from 'commons/src/analytics';
import { INTEGRATION_CLICKED_ADD, INTEGRATION_VIEWED_INTEGRATION } from 'commons/src/analytics/AnalyticsEvents';
import Error from 'commons/src/components/errorComponents/Error';
import SubHeader from 'commons/src/components/headers/SubHeader';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { userlane } from 'commons/src/components/placeholder';
import { roleRestrictions } from 'commons/src/constants';
import IntegrationRow from 'commons/src/features/integrations/IntegrationRow';
import { ActionButton } from 'commons/src/models/menuModels';
import { paths } from '../../../constants';
import { Store } from '../../../reducers';
import { useMqttPublisherConfigs } from './useMqttPublisherConfigs';

const analyticsObject = {
    pageType: PageType.Integration,
    page: IntegrationType.MQTT,
};

const MqttPublisherPage = (): React.ReactElement => {
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const { configs, loading, error, updateActiveState } = useMqttPublisherConfigs();
    const numberOfLocations = useSelector((state: Store) => state.locations.locations.length);

    useEffect(() => {
        analyticsLogger(INTEGRATION_VIEWED_INTEGRATION, analyticsObject);
    }, []);

    if (error) {
        return <Error />;
    }

    const clickAddPublisher = (): void => {
        navigate(`/${paths.addMqttPublisher}`);
        analyticsLogger(INTEGRATION_CLICKED_ADD, analyticsObject);
    };

    const clickEditPublisher = (id: string): void => {
        navigate(`/${paths.mqttPublisher}/${id}`);
    };

    const actionButtons: ActionButton[] = [
        {
            onClick: clickAddPublisher,
            title: 'MqttPublisher.NewPublisher',
            id: 'AddMqttPublisher',
            color: 'primary',
            testAttr: 'add-mqtt-publisher',
            icon: <MaterialIcon name="add" />,
            requiredRoleLevel: roleRestrictions.mqtt,
            requiredGroupTypes: [],
        },
    ];

    const sortedConfigs = [...configs].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div>
            <SubHeader actionButtons={numberOfLocations > 0 ? actionButtons : []} />
            <h2 className="settings__header settings__header--no-margin-top">{txt('MqttPublisher.MqttPublisher')}</h2>
            <div className="settings-details-container settings-details-container--margin">
                <div>{txt('MqttPublisher.MqttPublisherDescription')}</div>
                <a
                    href="https://developer-business.airthings.com/docs/mqtt-publisher-introduction"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {txt('MqttPublisher.LinkToDocs')}
                </a>
            </div>
            <ul className="list">
                <ReactPlaceholder ready={!(loading && configs.length === 0)} customPlaceholder={userlane}>
                    {sortedConfigs.length > 0 &&
                        sortedConfigs.map(config => (
                            <IntegrationRow
                                key={config.clientId}
                                id={config.clientId}
                                name={config.name}
                                active={config.active}
                                onClick={clickEditPublisher}
                                updateIntegration={(isOn: boolean): void => updateActiveState(config.clientId, isOn)}
                            />
                        ))}
                </ReactPlaceholder>
            </ul>
        </div>
    );
};

export default MqttPublisherPage;
